import React from 'react';
import { Alert, Segmented, Skeleton } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  CompatibilityUsageRoute,
  CompatibilityUsageSurface,
  getCompatibilityUsage,
} from '../../services/connectConfig';

type ConnectEnvironment = 'dev' | 'prod';

const WINDOW_OPTIONS = [7, 30, 90];

/** Shared so the page refresh button can refetch old-route evidence too. */
export function useCompatibilityUsage(environment: ConnectEnvironment, days: number) {
  return useQuery({
    queryKey: ['compatibilityUsage', environment, days],
    queryFn: () => getCompatibilityUsage(environment, days),
    retry: false,
  });
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Split a tracked route ("METHOD /path") into its method and path. Paths
 * never contain spaces, so the first space is always the separator; counted
 * rows are matched on the exact method + path pair.
 */
function splitTrackedRoute(tracked: string): { method: string; path: string } {
  const space = tracked.indexOf(' ');
  if (space === -1) return { method: tracked, path: '' };
  return { method: tracked.slice(0, space), path: tracked.slice(space + 1) };
}

/**
 * Calls to old API routes still seen on devices, grouped by surface. Used to
 * decide when a route can be deleted: retire it once it shows no calls in the
 * window, after the builds still calling it have updated.
 */
const CompatibilityUsageSection: React.FC<{
  environment: ConnectEnvironment;
}> = ({ environment }) => {
  const [days, setDays] = React.useState<number>(30);
  const query = useCompatibilityUsage(environment, days);
  // The window label always comes from the server-echoed report, never from
  // local state, so "no calls" can never describe the wrong window.
  const windowDays = query.data?.days ?? days;

  return (
    <section className="release-ledger release-usage" aria-labelledby="release-usage-title">
      <div className="release-section-intro release-ledger-intro">
        <div>
          <span>OLD ROUTES</span>
          <h3 id="release-usage-title">Which old routes apps still call</h3>
          <p>
            A route with no calls in the window below is a candidate to retire.
            Builds listed beside a route still call it and must update first.
          </p>
        </div>
        {query.data && (
          <small>Updated {new Date(query.data.generatedAt).toLocaleString('en')}</small>
        )}
      </div>

      <div className="release-usage-window">
        <span>Window</span>
        <Segmented
          value={days}
          onChange={(value) => setDays(value as number)}
          options={WINDOW_OPTIONS.map((option) => ({
            label: `${option} days`,
            value: option,
          }))}
        />
      </div>

      {query.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : query.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Old-route counts are temporarily unavailable"
          description={(query.error as any)?.response?.data?.error || 'Check the Connect deployment and try again.'}
        />
      ) : !query.data || query.data.surfaces.length === 0 ? (
        <div className="release-ledger-empty">
          <p>No old routes were counted in the last {windowDays} days.</p>
        </div>
      ) : (
        <>
          {query.data.surfaces.map((surface) => (
            <SurfaceBlock key={surface.surface} surface={surface} windowDays={windowDays} />
          ))}
          <div className="release-usage-cleanup" aria-label="Expired-link cleanup">
            <span>EXPIRED-LINK CLEANUP</span>
            {query.data.linkCleanup.days.length === 0 ? (
              <p>No cleanup runs were recorded in the last {windowDays} days.</p>
            ) : (
              <>
                <div className="release-versions-facts">
                  <div>
                    <span>Scheduled cleanup runs</span>
                    <strong>{query.data.linkCleanup.totals.scheduledRuns}</strong>
                  </div>
                  <div>
                    <span>Sessions deleted</span>
                    <strong>{query.data.linkCleanup.totals.deletedSessions}</strong>
                  </div>
                  <div>
                    <span>Runs with errors</span>
                    <strong>{query.data.linkCleanup.totals.runsWithErrors}</strong>
                  </div>
                </div>
                <p className="release-usage-note">In the last {windowDays} days.</p>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const countedRowKey = (route: CompatibilityUsageRoute) =>
  `${route.method} ${route.path} ${route.statusClass} ${route.build ?? ''}`;

/** One counted row as stored: method, path, status class and calls. */
function CountedRow({ route }: { route: CompatibilityUsageRoute }) {
  return (
    <div className="release-usage-row">
      <div className="release-ledger-version">
        <strong>{route.method} {route.path}</strong>
        <span>{route.statusClass} responses{route.build ? ` · ${route.build}` : ''}</span>
      </div>
      <span>{plural(route.requests, 'call')}</span>
      <span>Last seen {route.lastSeenDay}</span>
    </div>
  );
}

function SurfaceBlock({ surface, windowDays }: { surface: CompatibilityUsageSurface; windowDays: number }) {
  return (
    <div className="release-usage-surface">
      <div className="release-usage-surface-head">
        <strong>{surface.surface}</strong>
        <span>
          {plural(surface.requests, 'call')} in the last {windowDays} days
          {surface.lastSeenDay ? ` · last seen ${surface.lastSeenDay}` : ''}
        </span>
      </div>
      <div className="release-ledger-list release-usage-list">
        {surface.trackedRoutes === null
          ? surface.routes.map((route) => <CountedRow key={countedRowKey(route)} route={route} />)
          : surface.trackedRoutes.map((tracked) => {
            const { method, path } = splitTrackedRoute(tracked);
            const rows = surface.routes.filter(
              (route) => route.method === method && route.path === path,
            );
            const total = rows.reduce((sum, row) => sum + row.requests, 0);
            const lastSeen = rows.reduce<string | null>(
              (latest, row) => (latest === null || row.lastSeenDay > latest ? row.lastSeenDay : latest),
              null,
            );
            const builds = Array.from(new Set(rows.map((row) => row.build).filter((build) => build !== null)));
            return (
              <div className="release-usage-row" key={tracked}>
                <div className="release-ledger-version">
                  <strong>{method} {path}</strong>
                  {lastSeen === null ? (
                    <span>No calls in {windowDays} days</span>
                  ) : (
                    <span>Last seen {lastSeen}</span>
                  )}
                </div>
                <span className={lastSeen === null ? 'release-usage-quiet' : undefined}>
                  {plural(total, 'call')}
                </span>
                <span>
                  {builds.length === 0
                    ? '—'
                    : `Still called by ${builds.join(', ')}`}
                </span>
              </div>
            );
          })}
        {/* Calls counted on a tracked surface under a route missing from its
            list are shown too, never dropped. */}
        {surface.trackedRoutes !== null
          && surface.routes
            .filter((route) => !surface.trackedRoutes!.includes(`${route.method} ${route.path}`))
            .map((route) => <CountedRow key={countedRowKey(route)} route={route} />)}
      </div>
    </div>
  );
}

export default CompatibilityUsageSection;
